'use client'

import {useLayoutStore} from '../provider'
import React, {useCallback, useState} from 'react'

import {Copyright, FooterContacts, FooterMenu, FooterSocial, FooterSubscription} from './components'
import * as UI from './footer.styled'
import type {FooterModalsState} from './footer.types'
import {CallRequestModal, FeedbackModal} from './modals'

/**
 * Footer component - Main footer layout
 *
 * Decomposed from original 407-line component following SRP:
 * - FooterMenu: Navigation menu
 * - FooterContacts: Contact information
 * - FooterSocial: Social media links
 * - FooterSubscription: Email subscription form
 * - CallRequestModal: Call request form modal
 * - FeedbackModal: Feedback form modal
 *
 * Uses React useState instead of MobX for local modal state.
 * Uses INotificationService and IErrorReporter via hooks instead of direct imports.
 */
export const Footer: React.FC = () => {
  const layoutStore = useLayoutStore()

  const [modals, setModals] = useState<FooterModalsState>({
    callRequest: false,
    feedback: false,
  })

  const openCallRequest = useCallback(() => {
    setModals(prev => ({...prev, callRequest: true}))
  }, [])

  const closeCallRequest = useCallback(() => {
    setModals(prev => ({...prev, callRequest: false}))
  }, [])

  const openFeedback = useCallback(() => {
    setModals(prev => ({...prev, feedback: true}))
  }, [])

  const closeFeedback = useCallback(() => {
    setModals(prev => ({...prev, feedback: false}))
  }, [])

  return (
    <UI.Wrapper data-dark="true" data-testid="footer">
      <UI.Container>
        <UI.Left>
          <FooterMenu menu={layoutStore.data.footer.menu} />
        </UI.Left>
        <UI.Right>
          <UI.SubscribeColumn>
            <FooterSubscription />
          </UI.SubscribeColumn>
          <UI.InfoColumn>
            <UI.InfoContainer>
              <FooterContacts
                phone={layoutStore.data.footer.phone}
                onCallRequest={openCallRequest}
                onFeedback={openFeedback}
              />
              <FooterSocial socials={layoutStore.data.footer.socials} />
            </UI.InfoContainer>
            <Copyright />
          </UI.InfoColumn>
        </UI.Right>
      </UI.Container>

      <CallRequestModal open={modals.callRequest} onClose={closeCallRequest} />
      <FeedbackModal open={modals.feedback} onClose={closeFeedback} />
    </UI.Wrapper>
  )
}
